import {inject, Injectable} from '@angular/core';
import {AudioTrack} from '../audio/audio.service';
import {MediaItem as TimingMediaItem, MediaTimingService} from '../media-timing.service';
import {SlideshowDataService} from './slideshow-data.service';

interface MediaItem {
  url: string;
  type: 'image' | 'video';
  duration?: number;
}

@Injectable({
  providedIn: 'root'
})
export class AudioIntegrationService {
  private mediaTimingService = inject(MediaTimingService);
  private slideshowDataService = inject(SlideshowDataService);

  constructor() {
  }

  /**
   * Convert raw API results to properly formatted AudioTrack objects
   * This is critical for the audio service to work properly with timing and metadata
   */
  convertToAudioTracks(
    apiResults: any[],
    mediaItems: MediaItem[],
    currentDescription: string
  ): AudioTrack[] {
    console.log('Converting audio tracks, media items available:', mediaItems.length);

    return apiResults.map((result: any) => {
      const startTime = result.start_time || 0;

      // Get media items - if none provided, try to get from slideshow data service
      let currentMediaItems = mediaItems;

      // If no input media items, try to get from slideshow data service
      if (currentMediaItems.length === 0) {
        const serviceImages = this.slideshowDataService.slideshowImages();
        const serviceMediaItems = this.slideshowDataService.slideshowMediaItems();

        currentMediaItems = [
          ...serviceMediaItems,
          ...serviceImages.map(url => ({url, type: 'image' as const}))
        ];

        console.log('Using media items from service:', currentMediaItems.length);
      }

      // Debug logging for alternative prompts issue
      console.log('Converting track:', result.title, {
        startTime,
        originalEndTime: result.end_time,
        mediaItemsCount: currentMediaItems.length,
        hasMediaItems: currentMediaItems.length > 0
      });

      // Convert media items to format expected by MediaTimingService
      const timingMediaItems: TimingMediaItem[] = currentMediaItems.map(item => ({
        type: item.type,
        url: item.url,
        // Use the real measured video length when known; fall back to 30s.
        duration: item.type === 'video' ? (item.duration ?? 30) : undefined
      }));

      // Calculate end time using the shared service (same logic as live-demo component)
      // Always try to use the service first, but with better fallback logic
      let endTime: number;

      if (timingMediaItems.length > 0) {
        endTime = this.mediaTimingService.calculateEndTime(
          timingMediaItems,
          startTime,
          result.end_time
        );
      } else {
        // Fallback: if no media items available, calculate manually
        console.warn('No media items available for timing calculation, using manual calculation');

        if (result.end_time && result.end_time > startTime) {
          // Use API end_time if it's valid and greater than start_time
          endTime = result.end_time;
        } else {
          // Default to 30 seconds duration for alternative prompts
          endTime = startTime + 30;
        }
      }

      // Final safety check: ensure end time is always greater than start time
      if (endTime <= startTime) {
        console.warn(`Invalid end time calculated (${endTime}), falling back to start + 30 seconds`);
        endTime = startTime + 30;
      }

      console.log(`Track ${result.title}: startTime=${startTime}, endTime=${endTime}, duration=${endTime - startTime}`);

      return {
        url: result.url,
        title: result.title || 'Unknown Title',
        artist: result.artist || 'Unknown Artist',
        startTime: startTime,
        endTime: endTime,
        briefDescription: currentDescription
      };
    });
  }

  /**
   * Initialize and convert audio tracks for slideshow
   * This method combines track conversion with initialization logic
   */
  initializeAudioTracks(
    audioResults: any[],
    mediaItems: MediaItem[],
    currentDescription: string
  ): AudioTrack[] {
    if (audioResults.length === 0) {
      console.log('No audio results available for initialization');
      return [];
    }

    console.log('Initializing audio tracks', {
      trackCount: audioResults.length,
      mediaItemsCount: mediaItems.length
    });

    // Convert raw API results to properly formatted AudioTrack objects
    const audioTracks = this.convertToAudioTracks(audioResults, mediaItems, currentDescription);

    console.log('Audio tracks initialized:', audioTracks.length, 'tracks');

    return audioTracks;
  }
}
